import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { login as apiLogin, verify2FA } from "../api";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/ui";
import { getApiErrorMessage } from "../utils";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const passwordWasReset = searchParams.get("reset") === "1";

  const [brianPos, setBrianPos] = useState<{ x: number; y: number } | null>(null);

  // 2FA step state
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await apiLogin(email, password);
      if (res.data.requires_2fa) {
        setChallengeToken(res.data.challenge_token);
      } else {
        await login(res.data.access_token, res.data.refresh_token);
        navigate("/dashboard");
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await verify2FA(challengeToken ?? "", totpCode);
      await login(res.data.access_token, res.data.refresh_token);
      navigate("/dashboard");
    } catch (err) {
      setError(getApiErrorMessage(err, "Invalid code"));
      setTotpCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-xl rounded-xl bg-[#1D345F] px-5 py-6 shadow-lg sm:px-8 sm:pb-4">
        <h1 className="text-right text-2xl font-semibold text-white m-2">
          Betable{" "}
          <span
            onMouseEnter={(e) => setBrianPos({ x: e.clientX, y: e.clientY })}
            onMouseMove={(e) => setBrianPos({ x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setBrianPos(null)}
            className="cursor-default"
          >
            Brian
          </span>
        </h1>
        {brianPos && (
          <img
            src="/brian.jpg"
            alt="Brian"
            style={{
              position: "fixed",
              top: brianPos.y + 16,
              left: brianPos.x + 16,
              width: 200,
              height: "auto",
              pointerEvents: "none",
              zIndex: 9999,
              borderRadius: 8,
              boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
            }}
          />
        )}
        {challengeToken ? (
          <form onSubmit={handleVerify2FA} className="space-y-4">
            <p className="text-sm text-center text-gray-300">
              Enter the 6-digit code from your authenticator app.
            </p>
            <div>
              <label htmlFor="one-time-code" className="mb-1 block text-sm font-medium text-white">
                Authentication code
              </label>
              <input
                type="text"
                id="one-time-code"
                name="one-time-code"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white text-black tracking-widest text-center text-lg"
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <Button
              type="submit"
              disabled={loading || totpCode.length !== 6}
              className="w-full"
            >
              {loading ? "Verifying..." : "Verify"}
            </Button>
            <p className="text-center text-sm">
              <button
                type="button"
                onClick={() => { setChallengeToken(null); setError(""); setTotpCode(""); }}
                className="text-blue-400 hover:underline"
              >
                Back to sign in
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-white">Login</h2>
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-white">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white text-black"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-white">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white text-black"
              />
            </div>
            {passwordWasReset && (
              <p className="text-sm text-green-600">Password reset successfully. Please sign in.</p>
            )}
            {error &&
                <div className="flex items-center justify-center bg-red-500 p-2 rounded-lg">
                  <p className="text-sm text-white font-bold">
                    {error}
                  </p>
                </div>
            }
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-[#45a164] px-4 py-2 text-sm font-medium text-white hover:border-white border-2 border-[#1D345F] disabled:opacity-50 sm:w-1/2"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
              <p className="text-center text-sm text-white sm:text-right">
                <Link to="/forgot-password" className="text-white hover:underline">Forgot password?</Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
